import React, { useState, useEffect } from 'react';
import { leaveAPI, employeesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import ExportButton from '../components/ExportButton';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Plus, 
  Calendar as CalendarIcon, 
  Check, 
  X,
  Clock,
  Loader2
} from 'lucide-react';

const Leaves = () => {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [newLeave, setNewLeave] = useState({
    employee_id: '',
    leave_type: '',
    reason: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [leavesRes, employeesRes, typesRes] = await Promise.all([
        leaveAPI.getAll(),
        employeesAPI.getAll(),
        leaveAPI.getTypes()
      ]);
      
      const allLeaves = leavesRes.data;
      setLeaves(allLeaves);
      setPendingLeaves(allLeaves.filter(l => ['pending', 'manager_approved'].includes(l.status)));
      setEmployees(employeesRes.data);
      setLeaveTypes(typesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLeave = async () => {
    if (!newLeave.employee_id || !newLeave.leave_type || !startDate || !endDate) {
      toast.error('Please fill all required fields');
      return;
    }

    // Client-side 7-day advance warning for leaves >2 days
    const days = Math.round((endDate - startDate) / 86400000) + 1;
    const daysUntilStart = Math.round((startDate - new Date().setHours(0, 0, 0, 0)) / 86400000);
    const isEmergency = (newLeave.leave_type || '').toLowerCase().includes('emergency') ||
                        (newLeave.leave_type || '').toLowerCase().includes('bereavement');
    if (days > 2 && daysUntilStart < 7 && !isEmergency) {
      toast.error(`Leave requests longer than 2 days need at least 7 days notice (you've given ${daysUntilStart}). Mark as Emergency if urgent.`);
      return;
    }

    setSubmitting(true);
    try {
      await leaveAPI.create({
        ...newLeave,
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd')
      });
      toast.success('Leave request submitted');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit leave request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (leaveId, stage) => {
    try {
      await leaveAPI.approve(leaveId, stage);
      toast.success(stage ? `Leave ${stage}-approved` : 'Leave approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve leave');
    }
  };

  const handleReject = async (leaveId) => {
    try {
      await leaveAPI.reject(leaveId, 'Rejected by admin');
      toast.success('Leave rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject leave');
    }
  };

  // Decide next valid stage given role + current status
  const getNextLeaveStage = (leave) => {
    if (!user) return null;
    if (['approved', 'rejected', 'cancelled'].includes(leave.status)) return null;
    if (user.role === 'manager' && leave.status === 'pending') return 'manager';
    if (user.role === 'hr_admin' && ['pending', 'manager_approved'].includes(leave.status)) return 'hr';
    if (user.role === 'super_admin') {
      if (leave.status === 'pending') return 'manager';
      if (leave.status === 'manager_approved') return 'hr';
    }
    return null;
  };

  const LEAVE_STATUS_LABEL = {
    pending: 'Pending',
    manager_approved: 'Manager ✓',
    approved: 'Approved',
    rejected: 'Rejected',
    cancelled: 'Cancelled',
  };

  const resetForm = () => {
    setNewLeave({ employee_id: '', leave_type: '', reason: '' });
    setStartDate(null);
    setEndDate(null);
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown';
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'destructive';
      default: return 'secondary';
    }
  };

  const canApprove = ['super_admin', 'hr_admin', 'manager'].includes(user?.role);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Leave Management</h1>
          <p className="text-muted-foreground">Manage leave requests and balances</p>
        </div>
        <div className="flex gap-2">
        <ExportButton endpoint="/reports/leaves/export" filename="leaves" testId="export-leaves" />
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-hover" data-testid="apply-leave-btn">
              <Plus size={18} className="mr-2" />
              Apply Leave
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Apply for Leave</DialogTitle>
              <DialogDescription>
                Submit a new leave request
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Employee</Label>
                <Select 
                  value={newLeave.employee_id} 
                  onValueChange={(v) => setNewLeave({ ...newLeave, employee_id: v })}
                >
                  <SelectTrigger data-testid="leave-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Leave Type</Label>
                <Select 
                  value={newLeave.leave_type} 
                  onValueChange={(v) => setNewLeave({ ...newLeave, leave_type: v })}
                >
                  <SelectTrigger data-testid="leave-type-select">
                    <SelectValue placeholder="Select leave type" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...new Set(leaveTypes.map(lt => lt.name))].map(type => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="start-date-btn">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={setStartDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="end-date-btn">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Reason</Label>
                <Textarea
                  placeholder="Enter reason for leave..."
                  value={newLeave.reason}
                  onChange={(e) => setNewLeave({ ...newLeave, reason: e.target.value })}
                  data-testid="leave-reason"
                />
              </div>

              {(() => {
                if (!startDate || !endDate) return null;
                const days = Math.round((endDate - startDate) / 86400000) + 1;
                const daysUntilStart = Math.round((startDate - new Date().setHours(0, 0, 0, 0)) / 86400000);
                const isEmergency = (newLeave.leave_type || '').toLowerCase().includes('emergency') ||
                                    (newLeave.leave_type || '').toLowerCase().includes('bereavement');
                if (days > 2 && daysUntilStart < 7 && !isEmergency) {
                  return (
                    <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-3 text-sm text-amber-700 dark:text-amber-300" data-testid="leave-7day-warning">
                      ⚠ Leaves longer than 2 days must be submitted at least 7 days in advance. Current notice: {daysUntilStart} days.
                    </div>
                  );
                }
                return (
                  <div className="text-xs text-muted-foreground">Duration: {days} day(s) · Notice: {daysUntilStart} day(s)</div>
                );
              })()}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateLeave} disabled={submitting} data-testid="submit-leave-btn">
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Submit Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Clock className="text-amber-500" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Requests</p>
                <p className="text-2xl font-bold">{pendingLeaves.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <Check className="text-emerald-500" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Approved</p>
                <p className="text-2xl font-bold">
                  {leaves.filter(l => l.status === 'approved').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <X className="text-red-500" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Rejected</p>
                <p className="text-2xl font-bold">
                  {leaves.filter(l => l.status === 'rejected').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">
            Pending ({pendingLeaves.length})
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">
            All Requests
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Leave Type</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Days</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Reason</TableHead>
                    {canApprove && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingLeaves.length > 0 ? (
                    pendingLeaves.map((leave) => {
                      const nextStage = getNextLeaveStage(leave);
                      return (
                      <TableRow key={leave.id} data-testid={`leave-row-${leave.id}`}>
                        <TableCell className="font-medium">
                          {getEmployeeName(leave.employee_id)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{leave.leave_type}</Badge>
                        </TableCell>
                        <TableCell>
                          {leave.start_date} - {leave.end_date}
                        </TableCell>
                        <TableCell>{leave.days}</TableCell>
                        <TableCell>
                          <Badge variant={leave.status === 'manager_approved' ? 'default' : 'secondary'}>
                            {LEAVE_STATUS_LABEL[leave.status] || leave.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {leave.reason}
                        </TableCell>
                        {canApprove && (
                          <TableCell>
                            <div className="flex gap-2">
                              {nextStage && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-emerald-600 hover:bg-emerald-50"
                                  onClick={() => handleApprove(leave.id, nextStage)}
                                  data-testid={`approve-${leave.id}`}
                                  title={`Approve as ${nextStage}`}
                                >
                                  <Check size={14} className="mr-1" />
                                  <span className="text-xs">{nextStage}</span>
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleReject(leave.id)}
                                data-testid={`reject-${leave.id}`}
                              >
                                <X size={16} />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={canApprove ? 7 : 6} className="text-center py-8 text-muted-foreground">
                        No pending leave requests
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="all">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Leave Type</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Days</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Submitted</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaves.length > 0 ? (
                    leaves.map((leave) => (
                      <TableRow key={leave.id}>
                        <TableCell className="font-medium">
                          {getEmployeeName(leave.employee_id)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{leave.leave_type}</Badge>
                        </TableCell>
                        <TableCell>
                          {leave.start_date} - {leave.end_date}
                        </TableCell>
                        <TableCell>{leave.days}</TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(leave.status)}>
                            {leave.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(leave.created_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No leave requests found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Leaves;
